import React from 'react';
import styled from 'styled-components';
import LoadingSpinner from 'components/LoadingSpinner';

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 16px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const DemoCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
`;

const DemoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  flex-wrap: wrap;
  margin-bottom: ${(props) => props.marginBottom || '0'};
`;

const DemoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const DemoLabel = styled.span`
  font-size: 12px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CodeBlock = styled.pre`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.4)' : '#1e293b'};
  border-radius: 8px;
  padding: 20px;
  overflow-x: auto;
  margin: 0 0 24px 0;
  font-size: 13px;
  line-height: 1.6;
  color: #e2e8f0;
`;

const PropsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  font-size: 14px;

  th,
  td {
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid
      ${(props) =>
        props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  }

  th {
    font-weight: 600;
    color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc'};
  }

  td {
    color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  }

  code {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
  }
`;

const LoadingBox = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 8px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingSpinnerDocs = () => {
  return (
    <div>
      <PageTitle>Loading Spinner</PageTitle>
      <PageDescription>
        A customizable loading spinner component with theme support. Use it to
        indicate loading states in buttons, forms, tables, and other UI
        elements.
      </PageDescription>

      {/* Basic Usage */}
      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <SectionDescription>
          Import and use the LoadingSpinner component with default settings.
        </SectionDescription>
        <CodeBlock>
          {`import LoadingSpinner from 'components/LoadingSpinner';

// Basic spinner
<LoadingSpinner />

// Spinner with label
<LoadingSpinner label="Loading..." />`}
        </CodeBlock>
        <DemoCard>
          <DemoRow>
            <DemoItem>
              <LoadingSpinner />
              <DemoLabel>Default</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner label="Loading..." />
              <DemoLabel>With Label</DemoLabel>
            </DemoItem>
          </DemoRow>
        </DemoCard>
      </Section>

      {/* Sizes */}
      <Section>
        <SectionTitle>Sizes</SectionTitle>
        <SectionDescription>
          Customize the spinner size using the <code>size</code> prop. Adjust{' '}
          <code>borderWidth</code> for larger spinners.
        </SectionDescription>
        <CodeBlock>
          {`// Small (12px)
<LoadingSpinner size="12px" borderWidth="1.5px" />

// Default (16px)
<LoadingSpinner />

// Medium (24px)
<LoadingSpinner size="24px" borderWidth="3px" />

// Large (32px)
<LoadingSpinner size="32px" borderWidth="3px" />

// Extra Large (48px)
<LoadingSpinner size="48px" borderWidth="4px" />`}
        </CodeBlock>
        <DemoCard>
          <DemoRow>
            <DemoItem>
              <LoadingSpinner size="12px" borderWidth="1.5px" />
              <DemoLabel>Small (12px)</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner />
              <DemoLabel>Default (16px)</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner size="24px" borderWidth="3px" />
              <DemoLabel>Medium (24px)</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner size="32px" borderWidth="3px" />
              <DemoLabel>Large (32px)</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner size="48px" borderWidth="4px" />
              <DemoLabel>Extra Large (48px)</DemoLabel>
            </DemoItem>
          </DemoRow>
        </DemoCard>
      </Section>

      {/* Colors */}
      <Section>
        <SectionTitle>Colors</SectionTitle>
        <SectionDescription>
          Customize spinner color with the <code>color</code> prop. The default
          uses the theme&apos;s primary color (PitPay orange).
        </SectionDescription>
        <CodeBlock>
          {`// Primary (default)
<LoadingSpinner />

// Success (green)
<LoadingSpinner color="#22C55E" />

// Warning (yellow)
<LoadingSpinner color="#EAB308" />

// Error (red)
<LoadingSpinner color="#EF4444" />

// Info (blue)
<LoadingSpinner color="#3B82F6" />

// White (for dark backgrounds)
<LoadingSpinner color="#FFFFFF" trackColor="rgba(255,255,255,0.2)" />`}
        </CodeBlock>
        <DemoCard>
          <DemoRow>
            <DemoItem>
              <LoadingSpinner size="24px" borderWidth="3px" />
              <DemoLabel>Primary</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner size="24px" borderWidth="3px" color="#22C55E" />
              <DemoLabel>Success</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner size="24px" borderWidth="3px" color="#EAB308" />
              <DemoLabel>Warning</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner size="24px" borderWidth="3px" color="#EF4444" />
              <DemoLabel>Error</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner size="24px" borderWidth="3px" color="#3B82F6" />
              <DemoLabel>Info</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingBox
                style={{ background: '#1e293b', padding: '16px 24px' }}
              >
                <LoadingSpinner
                  size="24px"
                  borderWidth="3px"
                  color="#FFFFFF"
                  trackColor="rgba(255,255,255,0.2)"
                />
              </LoadingBox>
              <DemoLabel>White</DemoLabel>
            </DemoItem>
          </DemoRow>
        </DemoCard>
      </Section>

      {/* Animation Speed */}
      <Section>
        <SectionTitle>Animation Speed</SectionTitle>
        <SectionDescription>
          Control the spin speed with the <code>speed</code> prop.
        </SectionDescription>
        <CodeBlock>
          {`// Fast (0.5s)
<LoadingSpinner speed="0.5s" />

// Default (0.8s)
<LoadingSpinner />

// Slow (1.2s)
<LoadingSpinner speed="1.2s" />`}
        </CodeBlock>
        <DemoCard>
          <DemoRow>
            <DemoItem>
              <LoadingSpinner size="24px" borderWidth="3px" speed="0.5s" />
              <DemoLabel>Fast (0.5s)</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner size="24px" borderWidth="3px" />
              <DemoLabel>Default (0.8s)</DemoLabel>
            </DemoItem>
            <DemoItem>
              <LoadingSpinner size="24px" borderWidth="3px" speed="1.2s" />
              <DemoLabel>Slow (1.2s)</DemoLabel>
            </DemoItem>
          </DemoRow>
        </DemoCard>
      </Section>

      {/* With Labels */}
      <Section>
        <SectionTitle>With Labels</SectionTitle>
        <SectionDescription>
          Add descriptive text next to the spinner using the <code>label</code>{' '}
          prop. Customize label color with <code>labelColor</code>.
        </SectionDescription>
        <CodeBlock>
          {`<LoadingSpinner label="Loading..." />

<LoadingSpinner label="Processing payment..." />

<LoadingSpinner
  label="Saving changes..."
  labelColor="#22C55E"
  color="#22C55E"
/>`}
        </CodeBlock>
        <DemoCard>
          <DemoRow>
            <LoadingSpinner label="Loading..." />
          </DemoRow>
          <DemoRow marginBottom="16px" style={{ marginTop: '16px' }}>
            <LoadingSpinner label="Processing payment..." />
          </DemoRow>
          <DemoRow style={{ marginTop: '16px' }}>
            <LoadingSpinner
              label="Saving changes..."
              labelColor="#22C55E"
              color="#22C55E"
            />
          </DemoRow>
        </DemoCard>
      </Section>

      {/* Common Use Cases */}
      <Section>
        <SectionTitle>Common Use Cases</SectionTitle>
        <SectionDescription>
          Examples of how to use the spinner in different contexts.
        </SectionDescription>

        <SectionTitle
          as="h3"
          style={{ fontSize: '16px', marginTop: '24px', marginBottom: '12px' }}
        >
          In TableFilter (Dynamic Filter Options)
        </SectionTitle>
        <CodeBlock>
          {`// When filter options are loading from API
const filterConfig = [
  {
    key: 'division',
    label: 'Division',
    type: 'select',
    options: divisions || [],
    loading: divisionsLoading,
    loadingText: 'Loading divisions...',
    disabled: divisionsLoading,
  },
];`}
        </CodeBlock>

        <SectionTitle
          as="h3"
          style={{ fontSize: '16px', marginTop: '24px', marginBottom: '12px' }}
        >
          Inline Loading State
        </SectionTitle>
        <CodeBlock>
          {`import LoadingSpinner from 'components/LoadingSpinner';

// In a form or card
{isLoading ? (
  <LoadingSpinner label="Loading options..." />
) : (
  <Select options={options} />
)}`}
        </CodeBlock>

        <SectionTitle
          as="h3"
          style={{ fontSize: '16px', marginTop: '24px', marginBottom: '12px' }}
        >
          Centered Loading Container
        </SectionTitle>
        <CodeBlock>
          {`const LoadingContainer = styled.div\`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
\`;

<LoadingContainer>
  <LoadingSpinner
    size="32px"
    borderWidth="3px"
    label="Loading data..."
  />
</LoadingContainer>`}
        </CodeBlock>
        <DemoCard>
          <LoadingBox style={{ padding: '48px' }}>
            <LoadingSpinner
              size="32px"
              borderWidth="3px"
              label="Loading data..."
            />
          </LoadingBox>
        </DemoCard>
      </Section>

      {/* Props Table */}
      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable>
          <thead>
            <tr>
              <th>Prop</th>
              <th>Type</th>
              <th>Default</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>size</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>
                <code>&apos;16px&apos;</code>
              </td>
              <td>Width and height of the spinner</td>
            </tr>
            <tr>
              <td>
                <code>color</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>
                <code>theme.primary</code>
              </td>
              <td>Color of the spinning arc</td>
            </tr>
            <tr>
              <td>
                <code>trackColor</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>
                <code>theme-aware gray</code>
              </td>
              <td>Color of the background track</td>
            </tr>
            <tr>
              <td>
                <code>borderWidth</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>
                <code>&apos;2px&apos;</code>
              </td>
              <td>Thickness of the spinner border</td>
            </tr>
            <tr>
              <td>
                <code>speed</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>
                <code>&apos;0.8s&apos;</code>
              </td>
              <td>Duration of one full rotation</td>
            </tr>
            <tr>
              <td>
                <code>label</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>-</td>
              <td>Optional text to display next to spinner</td>
            </tr>
            <tr>
              <td>
                <code>labelColor</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>
                <code>theme-aware gray</code>
              </td>
              <td>Color of the label text</td>
            </tr>
            <tr>
              <td>
                <code>gap</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>
                <code>&apos;8px&apos;</code>
              </td>
              <td>Space between spinner and label</td>
            </tr>
            <tr>
              <td>
                <code>className</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>-</td>
              <td>Additional CSS class for styling</td>
            </tr>
          </tbody>
        </PropsTable>
      </Section>

      {/* Exports */}
      <Section>
        <SectionTitle>Exports</SectionTitle>
        <SectionDescription>
          The component exports additional utilities for custom implementations.
        </SectionDescription>
        <CodeBlock>
          {`// Default export
import LoadingSpinner from 'components/LoadingSpinner';

// Named exports for custom styling
import { Spinner, SpinnerWrapper, Label, spin } from 'components/LoadingSpinner';

// 'spin' is the keyframes animation for creating custom spinners
const CustomSpinner = styled.div\`
  animation: \${spin} 0.8s linear infinite;
\`;`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default LoadingSpinnerDocs;
